import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from './api';

export type Role = 'admin' | 'oversight' | 'manager' | 'staff' | 'account';

export interface User {
  id: number;
  email: string;
  name: string;
  phone: string | null;
  role: Role;
  image_url: string | null;
  is_active?: boolean;
  created_at?: string;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
  setUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore the session from the httpOnly cookie on app load. There is no
  // client-side token anymore; the cookie is sent on every request.
  useEffect(() => {
    let active = true;
    api
      .get<User>('/auth/profile')
      .then((data) => {
        if (active) setUser(data);
      })
      .catch(() => {
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onUnauthorized = () => {
      api.post('/auth/logout').catch(() => {});
      setUser(null);
    };
    window.addEventListener('nes:unauthorized', onUnauthorized);
    return () => window.removeEventListener('nes:unauthorized', onUnauthorized);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api.post<{ user: User }>('/auth/login', { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    api.post('/auth/logout').catch(() => {});
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await api.get<User>('/auth/profile');
      setUser(data);
    } catch {
      setUser(null);
    }
  }, []);

  const hasRole = useCallback((...roles: Role[]) => !!user && roles.includes(user.role), [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, hasRole, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}